import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useSelector, useDispatch} from 'react-redux';

import {
  selectApplicantInformation,
  selectHasCrmPermission,
  selectUserInfo,
} from 'src/selectors/crm';
import {updateCrmApplicantInformationData} from 'src/action-creators/crm';

import {currencies} from 'src/components/crm/lib/filters/candidate-filters/constant';
import {numberByFormat} from 'src/components/crm/lib/custom-fields/view-custom-fields';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Combobox} from '@spaced-out/ui-design-system/lib/components/Combobox';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {RadioButton} from '@spaced-out/ui-design-system/lib/components/RadioButton';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';

import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';

import {TypeAheadSelector} from 'src/components/crm/lib/typeAhead/typeahead';

import {
  Toast,
  TOAST_SEMANTIC,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';

import {
  BodyMedium,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';

import * as reduxApi from 'src/utils/redux-api-v2';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';

import css from './default-fields-card.css';


const DefaultFieldsCard = ({
  showEditButton = true,
}: {
  showEditButton?: boolean;
}) => {
  const {t, language} = useLabelTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const editCandidateDetails = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.EDIT_CANDIDATE_DETAILS,
  );
  const viewCandidateCTC = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_CANDIDATE_CTC,
  );
  const [isEditing, setIsEditing] = React.useState(false);
  const applicantDetails = useSelector(selectApplicantInformation);

  const {resume} = applicantDetails ?? {};
  const {
    endSalary,
    // @ts-expect-error - TS2339 - Property 'endSalaryCurrency' does not exist on type 'Resume'.
    endSalaryCurrency,
    salaryExpectation,
    // @ts-expect-error - TS2339 - Property 'salaryExpectationCurrency' does not exist on type 'Resume'.
    salaryExpectationCurrency,
    noticePeriod,
    preferredLocations,
  } = resume ?? {};

  const initialFormState = {
    endSalary: endSalary ?? 0,
    endSalaryCurrency: endSalaryCurrency ?? '',
    salaryExpectation: salaryExpectation ?? 0,
    salaryExpectationCurrency: salaryExpectationCurrency ?? '',
    noticePeriod: noticePeriod ?? 0,
    preferredLocations: preferredLocations ?? [],
  } as const;
  const [formState, setFormState] = React.useState(initialFormState);
  const [isSaving, setIsSaving] = React.useState(false);

  // @ts-expect-error - TS7006 - Parameter 'formState' implicitly has an 'any' type.
  const handleUpdateFormState = (formState) => {
    setFormState(formState);
  };

  // Check if any validation is required
  const isFormValidated = true;

  // @ts-expect-error - TS7030 - Not all code paths return a value.
  const getNoticePeriod = (noticePeriod: number) => {
    if (!noticePeriod || noticePeriod === -999 || noticePeriod === 0) {
      return '-';
    }
    if (noticePeriod === -1) {
      return t('Available_Immediately', 'Available Immediately');
    }
    if (noticePeriod === -2) {
      return t('Serving_Notice_Period', 'Serving Notice Period');
    }
    if (noticePeriod > 0) {
      return `${noticePeriod} ${t('Days', 'Days')}`;
    }
  };

  const handleSave = async () => {
    if (isFormValidated) {
      try {
        setIsSaving(true);

        const response = await dispatch(
          reduxApi.post(
            `api/candidates/${applicantDetails?.id}`,
            {
              section: 'SALARY_INFO',
              editFields: formState,
            },
            {},
            {
              apiPath: '/trmapi/',
            },
          ),
        );

        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>{`${t(
              'Candidate_Details_Updated_Successfully',
              'Candidate Details Updated Successfully',
            )}!`}</ToastTitle>
          </Toast>,
        );

        setIsEditing(false);

        dispatch(
          updateCrmApplicantInformationData({
            ...applicantDetails,
            ...response,
          }),
        );
      } catch (e: any) {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>
              {t(
                'Failed_to_save_candidate_details',
                'Failed to save candidate details',
              )}
            </ToastTitle>
          </Toast>,
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div>
      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon name="info-circle" size="small" type="solid" />
            {t('System_Fields', 'System Fields')}
          </CardTitle>
          {editCandidateDetails && showEditButton && (
            <CardActions>
              <Button
                iconLeftName="pen"
                type="ghost"
                size="small"
                onClick={() => setIsEditing(true)}
              />
            </CardActions>
          )}
        </CardHeader>
        <CardContent>
          <div className={css.candidateDetailsCardContentContainer}>
            {viewCandidateCTC && (
              <div>
                <BodySmall>{t('Current_CTC', 'Current CTC')}</BodySmall>
                {/* @ts-expect-error - TS18048 - 'endSalary' is possibly 'undefined'. */}
                {endSalary > 0 ? (
                  <BodyMedium className={css.bold}>{`${numberByFormat(
                    // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string | number'.
                    endSalary,
                    language,
                  )} ${
                    endSalaryCurrency ? endSalaryCurrency : ''
                  }`}</BodyMedium>
                ) : (
                  '-'
                )}
              </div>
            )}
            {viewCandidateCTC && (
              <div className={css.candidateDetailBlock}>
                <BodySmall>{t('Expected_CTC', 'Expected CTC')}</BodySmall>
                <BodyMedium className={css.bold}>
                  {/* @ts-expect-error - TS18048 - 'salaryExpectation' is possibly 'undefined'. */}
                  {salaryExpectation > 0
                    ? // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string | number'.
                      `${numberByFormat(salaryExpectation, language)} ${
                        salaryExpectationCurrency
                          ? salaryExpectationCurrency
                          : ''
                      }`
                    : '-'}
                </BodyMedium>
              </div>
            )}
            <div className={css.candidateDetailBlock}>
              <BodySmall>{t('Notice_Period', 'Notice Period')}</BodySmall>
              <BodyMedium className={css.bold}>
                {/* @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'number'. */}
                {getNoticePeriod(noticePeriod)}
              </BodyMedium>
            </div>
            <div className={css.candidateDetailBlock}>
              <BodySmall>
                {t('Preferred_Locations', 'Preferred Locations')}
              </BodySmall>
              <div className={css.candidateEmailsContainer}>
                {(preferredLocations || []).length > 0
                  ? // @ts-expect-error - TS18048 - 'preferredLocations' is possibly 'undefined'.
                    preferredLocations.map((location) => (
                      <Chip
                        semantic="primary"
                        size="medium"
                        key={location}
                        dismissable={false}
                      >
                        {location}
                      </Chip>
                    ))
                  : '-'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isEditing ? (
        <Panel
          isOpen={true}
          anchor="right"
          onClose={() => setIsEditing(false)}
          hideBackdrop={false}
        >
          <PanelHeader onCloseButtonClick={() => setIsEditing(false)}>
            {t('Edit_System_Fields', 'Edit System Fields')}
          </PanelHeader>
          <PanelBody>
            <EditSystemFields
              formState={formState}
              handleUpdateFormState={handleUpdateFormState}
            />
          </PanelBody>
          <PanelFooter>
            <Button
              type="tertiary"
              size="small"
              onClick={() => {
                setIsEditing(false);
                setFormState(initialFormState);
              }}
            >
              {t('Cancel', 'Cancel')}
            </Button>
            <Button size="small" onClick={handleSave} isLoading={isSaving}>
              {t('Save', 'Save')}
            </Button>
          </PanelFooter>
        </Panel>
      ) : null}
    </div>
  );
};

export default DefaultFieldsCard;

type EditSystemFieldsProps = {
  formState: {
    endSalary: number;
    endSalaryCurrency: string;
    salaryExpectation: number;
    salaryExpectationCurrency: string;
    noticePeriod: number;
    preferredLocations: string[];
  };
  handleUpdateFormState: (
    formState: EditSystemFieldsProps['formState'],
  ) => void;
};

const EditSystemFields = ({
  formState,
  handleUpdateFormState,
}: EditSystemFieldsProps) => {
  const {t, makeKey} = useLabelTranslation();
  const [selectedCustomNoticePeriod, setSelectedCustomNoticePeriod] =
    React.useState(
      Boolean(formState.noticePeriod) && formState.noticePeriod >= 0,
    );

  return (
    <div className={css.editSystemFieldsContainer}>
      <Combobox
        inputPlaceholder={`${t('Enter_amount', 'Enter amount')}...`}
        label={t('Current_CTC', 'Current CTC')}
        menu={{
          options: currencies.map((currency) => ({
            key: currency.value,
            label: t(makeKey(currency.label), currency.label),
          })),
          selectedKeys: formState.endSalaryCurrency
            ? [formState.endSalaryCurrency]
            : [],
        }}
        onChange={({dropdownOption, input}) => {
          if (dropdownOption) {
            handleUpdateFormState({
              ...formState,
              endSalaryCurrency: dropdownOption.key,
            });
          } else {
            const numberInput = input.replace(/\D/g, '');
            handleUpdateFormState({
              ...formState,
              endSalary: Number(numberInput),
            });
          }
        }}
        value={{
          dropdown: formState.endSalaryCurrency,
          input: formState.endSalary < 0 ? '0' : formState.endSalary.toString(),
        }}
      />
      <Combobox
        inputPlaceholder="Enter amount..."
        label="Expected CTC"
        menu={{
          options: currencies.map((currency) => ({
            key: currency.value,
            label: currency.label,
          })),
          selectedKeys: formState.salaryExpectationCurrency
            ? [formState.salaryExpectationCurrency]
            : [],
        }}
        // @ts-expect-error - TS2769 - No overload matches this call.
        input={{
          type: 'number',
        }}
        onChange={({dropdownOption, input}) => {
          if (dropdownOption) {
            handleUpdateFormState({
              ...formState,
              salaryExpectationCurrency: dropdownOption.key,
            });
          } else {
            const numberInput = input.replace(/\D/g, '');
            handleUpdateFormState({
              ...formState,
              salaryExpectation: Number(numberInput),
            });
          }
        }}
        value={{
          dropdown: formState.salaryExpectationCurrency,
          input:
            formState.salaryExpectation < 0
              ? '0'
              : formState.salaryExpectation.toString(),
        }}
      />
      <BodySmall>{t('Notice_Period', 'Notice Period')}</BodySmall>
      <RadioButton
        value={'0'}
        onChange={() => {
          handleUpdateFormState({
            ...formState,
            noticePeriod: 0,
          });
          setSelectedCustomNoticePeriod(true);
        }}
        selectedValue={selectedCustomNoticePeriod ? '0' : ''}
      >
        {t('Notice_to_Serve', 'Notice to Serve')}
      </RadioButton>
      {selectedCustomNoticePeriod ? (
        <Input
          type="number"
          min={'0'}
          value={formState.noticePeriod.toString()}
          onChange={(e) => {
            handleUpdateFormState({
              ...formState,
              noticePeriod: Number(e.target.value),
            });
          }}
        />
      ) : null}
      <RadioButton
        value={'-2'}
        onChange={() => {
          handleUpdateFormState({
            ...formState,
            noticePeriod: -2,
          });
          setSelectedCustomNoticePeriod(false);
        }}
        selectedValue={formState.noticePeriod.toString()}
      >
        {t('Currently_Serving_notice', 'Currently Serving notice')}
      </RadioButton>
      <RadioButton
        value={'-1'}
        onChange={() => {
          handleUpdateFormState({
            ...formState,
            noticePeriod: -1,
          });
          setSelectedCustomNoticePeriod(false);
        }}
        selectedValue={formState.noticePeriod.toString()}
      >
        {t('Immediately_Available', 'Immediately Available')}
      </RadioButton>
      <TypeAheadSelector
        label={t('Preferred_Locations', 'Preferred Locations')}
        isApiCall={false}
        isUserSearch={false}
        typeAheadOptions={[]}
        placeholder={`${t('Enter_location', 'Enter location')}...`}
        selectedOption={formState.preferredLocations.map((location) => ({
          key: location,
          label: location,
        }))}
        onSelectOption={(options: Array<MenuOption>) => {
          handleUpdateFormState({
            ...formState,
            preferredLocations: options.map((option) => option.key),
          });
        }}
      />
    </div>
  );
};
